import React, { useState, useEffect, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fonction pour obtenir l'URL de l'image
function getImageUrl(imagePath) {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  const filename = imagePath.split('/').pop();
  return `${BACKEND_URL}/api/images/${filename}`;
}

// Fonction pour formater la durée
function formatDuration(sec) {
  if (!sec || isNaN(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function EmbedPlayer() {
  const [track, setTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const audioRef = useRef(new Audio());

  // Extraire l'ID de la piste depuis l'URL
  const getTrackIdFromUrl = () => {
    const path = window.location.hash || window.location.pathname;
    const match = path.match(/\/embed\/track\/(\d+)/);
    return match ? match[1] : null;
  };

  // Charger les données de la piste
  useEffect(() => {
    const trackId = getTrackIdFromUrl();
    if (!trackId) {
      setError('ID de piste non trouvé');
      setLoading(false);
      return;
    }

    fetch(`${BACKEND_URL}/api/tracks/${trackId}`)
      .then(res => {
        if (!res.ok) throw new Error('Piste non trouvée');
        return res.json();
      })
      .then(data => {
        setTrack(data);
        setLoading(false);
        
        // Configurer l'audio
        let audioUrl;
        if (data.file_path.startsWith('http://') || data.file_path.startsWith('https://')) {
          audioUrl = data.file_path;
        } else {
          const filename = data.file_path.split('/').pop();
          audioUrl = `${BACKEND_URL}/api/audio/${filename}`;
        }
        audioRef.current.src = audioUrl;
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // Gérer les événements audio
  useEffect(() => {
    const audio = audioRef.current;

    const handleTimeUpdate = () => {
      if (audio.duration) {
        setProgress((audio.currentTime / audio.duration) * 100);
        setCurrentTime(audio.currentTime);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.pause();
    };
  }, []);

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = ((e.clientX - rect.left) / rect.width) * 100;
    const newTime = (percent / 100) * audioRef.current.duration;
    audioRef.current.currentTime = newTime;
    setProgress(percent);
  };

  if (loading) {
    return (
      <div className="w-full h-full min-h-[152px] bg-gradient-to-r from-zinc-900 to-zinc-800 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !track) {
    return (
      <div className="w-full h-full min-h-[152px] bg-gradient-to-r from-zinc-900 to-zinc-800 flex items-center justify-center text-white/60">
        {error || 'Piste non trouvée'}
      </div>
    );
  }

  return (
    <div className="w-full bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-900 p-4 font-sans">
      <div className="flex gap-4">
        {/* Cover Image */}
        <div className="w-28 h-28 rounded-lg overflow-hidden flex-shrink-0 shadow-lg">
          {track.cover_image ? (
            <img 
              src={getImageUrl(track.cover_image)} 
              alt={track.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-4xl">
              {track.cover_emoji || '🎵'}
            </div>
          )}
        </div>

        {/* Track Info & Controls */}
        <div className="flex-1 flex flex-col justify-between min-w-0">
          {/* Info */}
          <div>
            <h3 className="text-white font-semibold text-lg truncate">{track.title}</h3>
            <p className="text-white/60 text-sm truncate">{track.artist}</p>
          </div>

          {/* Progress Bar */}
          <div className="mt-2">
            <div 
              className="w-full h-1 bg-white/20 rounded-full cursor-pointer"
              onClick={handleSeek}
            >
              <div 
                className="h-full bg-green-500 rounded-full transition-all duration-150"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-white/60">
              <span>{formatDuration(currentTime)}</span>
              <span>{formatDuration(track.duration)}</span>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-4 mt-2">
            <button
              onClick={togglePlay}
              className="w-10 h-10 rounded-full bg-green-500 hover:bg-green-400 flex items-center justify-center text-black transition-colors"
            >
              {isPlaying ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                </svg>
              ) : (
                <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              )}
            </button>

            {/* Vinisys Music branding */}
            <a 
              href={`${BACKEND_URL}/#/`}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto flex items-center gap-2 text-white/40 hover:text-white/60 transition-colors"
            >
              <span className="text-xs">Vinisys Music</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
